import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Save,
  AlertTriangle,
  Clock,
  Settings,
  Edit3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Policy Editor
 * 
 * Allows administrators to edit:
 * - Call-Out Policy
 * - No-Show Policy
 * - Attendance & Tardiness Policy
 */

const POLICY_TYPES = [
  { 
    key: 'callout', 
    label: 'Call-Out Policy',
    description: 'Rules for reporting absences from scheduled shifts',
    icon: AlertTriangle,
    color: 'amber'
  },
  { 
    key: 'no_show', 
    label: 'No-Call/No-Show Policy',
    description: 'Consequences for failing to report absences',
    icon: AlertTriangle,
    color: 'red'
  },
  { 
    key: 'attendance', 
    label: 'Attendance & Tardiness Policy',
    description: 'Expectations for punctuality and presence',
    icon: Clock,
    color: 'blue'
  },
];

export default function PolicyEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policies, setPolicies] = useState({});
  const [selectedPolicy, setSelectedPolicy] = useState('callout');
  const [editedPolicy, setEditedPolicy] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  useEffect(() => {
    if (policies[selectedPolicy]) {
      setEditedPolicy({ ...policies[selectedPolicy] });
      setHasChanges(false);
    }
  }, [selectedPolicy, policies]);

  const fetchPolicies = async () => {
    try {
      const res = await fetch(`${API_URL}/api/coverage/policies`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPolicies(data.policies || {});
      }
    } catch (err) {
      console.error('Error fetching policies:', err);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedPolicy(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!editedPolicy) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/coverage/policies/${selectedPolicy}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editedPolicy.title,
          content: editedPolicy.content,
          acknowledgment_text: editedPolicy.acknowledgment_text,
          notice_hours: editedPolicy.notice_hours,
          max_callouts_per_month: editedPolicy.max_callouts_per_month,
          tardiness_threshold_minutes: editedPolicy.tardiness_threshold_minutes,
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPolicies(prev => ({ ...prev, [selectedPolicy]: data.policy }));
        setHasChanges(false);
        toast.success('Policy saved successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save policy');
      }
    } catch (err) {
      console.error('Error saving policy:', err);
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'amber': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'red': return 'bg-red-100 text-red-700 border-red-200';
      case 'blue': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  const currentPolicyConfig = POLICY_TYPES.find(p => p.key === selectedPolicy);
  const PolicyIcon = currentPolicyConfig?.icon || FileText;

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization/settings')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Settings
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#176DFF] to-[#29C0FF] flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#0D1B33]">Policy Editor</h1>
                <p className="text-sm text-[#64748B]">Edit caregiver policies and acknowledgments</p>
              </div>
            </div>
            {hasChanges && (
              <Button onClick={handleSave} disabled={saving} className="bg-[#176DFF] hover:bg-[#176DFF]/90">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            )}
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Policy Selector */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-medium text-[#0D1B33] text-sm">Policies</h2>
              </div>
              <div className="p-2">
                {POLICY_TYPES.map((policy) => {
                  const Icon = policy.icon;
                  const isSelected = selectedPolicy === policy.key;
                  
                  return (
                    <button
                      key={policy.key}
                      onClick={() => setSelectedPolicy(policy.key)}
                      className={`w-full p-3 rounded-lg text-left transition-all mb-1 ${
                        isSelected
                          ? 'bg-[#176DFF]/10 border-2 border-[#176DFF]'
                          : 'hover:bg-[#F8FAFC] border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getColorClasses(policy.color)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className={`font-medium text-sm ${isSelected ? 'text-[#176DFF]' : 'text-[#0D1B33]'}`}>
                          {policy.label}
                        </span>
                      </div>
                      <p className="text-xs text-[#64748B] ml-10">{policy.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Policy Editor */}
          <div className="lg:col-span-3 space-y-6">
            {editedPolicy && (
              <>
                {/* Policy Header */}
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(currentPolicyConfig?.color)}`}>
                      <PolicyIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={editedPolicy.title || ''}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        className="text-lg font-semibold border-none shadow-none px-0 focus-visible:ring-0"
                        placeholder="Policy Title"
                      />
                    </div>
                    {editedPolicy.is_default && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        Default Template
                      </span>
                    )}
                  </div>

                  {/* Policy Parameters */}
                  {selectedPolicy === 'callout' && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-[#F8FAFC] rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                          Required Notice (hours)
                        </label>
                        <Input
                          type="number"
                          value={editedPolicy.notice_hours || 4}
                          onChange={(e) => handleFieldChange('notice_hours', parseInt(e.target.value))}
                          min={1}
                          max={48}
                        />
                        <p className="text-xs text-[#64748B] mt-1">Hours before shift</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                          Max Callouts/Month
                        </label>
                        <Input
                          type="number"
                          value={editedPolicy.max_callouts_per_month || 3}
                          onChange={(e) => handleFieldChange('max_callouts_per_month', parseInt(e.target.value))}
                          min={1}
                          max={10}
                        />
                        <p className="text-xs text-[#64748B] mt-1">Before HR review</p>
                      </div>
                    </div>
                  )}

                  {selectedPolicy === 'attendance' && (
                    <div className="p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="max-w-xs">
                        <label className="block text-sm font-medium text-[#0D1B33] mb-1">
                          Tardiness Threshold (minutes)
                        </label>
                        <Input
                          type="number"
                          value={editedPolicy.tardiness_threshold_minutes || 5}
                          onChange={(e) => handleFieldChange('tardiness_threshold_minutes', parseInt(e.target.value))}
                          min={1}
                          max={30}
                        />
                        <p className="text-xs text-[#64748B] mt-1">Minutes late before marked tardy</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Policy Content */}
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Edit3 className="w-5 h-5 text-[#176DFF]" />
                    <h3 className="font-semibold text-[#0D1B33]">Policy Content</h3>
                  </div>
                  <p className="text-sm text-[#64748B] mb-3">
                    This text is shown to caregivers before they report a callout. Use **bold** for emphasis.
                  </p>
                  <textarea
                    value={editedPolicy.content || ''}
                    onChange={(e) => handleFieldChange('content', e.target.value)}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] font-mono text-sm"
                    rows={12}
                    placeholder="Enter policy content..."
                  />
                  <p className="text-xs text-[#64748B] mt-2">
                    Tip: Use • for bullet points and **text** for bold
                  </p>
                </div>

                {/* Acknowledgment Text */}
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-[#0D1B33]">Acknowledgment Text</h3>
                  </div>
                  <p className="text-sm text-[#64748B] mb-3">
                    Caregivers must sign to confirm they understand this text.
                  </p>
                  <textarea
                    value={editedPolicy.acknowledgment_text || ''}
                    onChange={(e) => handleFieldChange('acknowledgment_text', e.target.value)}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] text-sm"
                    rows={4}
                    placeholder="Enter acknowledgment text..."
                  />
                </div>

                {/* Preview */}
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
                  <h3 className="font-semibold text-amber-900 mb-3">Preview (as caregivers will see it)</h3>
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-[#0D1B33] mb-3">{editedPolicy.title}</h4>
                    <div 
                      className="prose prose-sm max-w-none text-[#0D1B33]"
                      dangerouslySetInnerHTML={{ 
                        __html: (editedPolicy.content || '').replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/•/g, '<br>•')
                      }}
                    />
                  </div>
                </div>

                {/* Save Button (Mobile) */}
                {hasChanges && (
                  <div className="lg:hidden">
                    <Button onClick={handleSave} disabled={saving} className="w-full bg-[#176DFF] hover:bg-[#176DFF]/90">
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
